/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import {
    IAddressAddItem,
    IAddressAddUpdateProps,
    IAddressSelectItem,
    IAddressSelectProps,
    IAddressShowItem,
    IAddressShowProps
} from '@msdyn365-commerce-modules/address';
import { ICheckoutShippingAddressViewProps } from './custom-checkout-shipping-address';
import { convert_a2_to_a3 } from '../../themes/venchi-v2-1/utils/convert_a2_a3';
import { RichTextComponent } from '@msdyn365-commerce/core-internal';
import { cleanAddress } from '../../common/utils/address';

const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items &&
                items.map((item: IAddressShowItem) => {
                    return <>{cleanAddress(item.description)}</>;
                })}
        </Node>
    );
};

const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...SelectAddress}>
            {addButton}
            {items &&
                items.map((item: IAddressSelectItem) => {
                    const SelectItem = item.SelectItem;
                    return (
                        <Node {...SelectItem} key={item.key}>
                            {item.input}
                            <AddressShow {...item.showItems} />
                        </Node>
                    );
                })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

// tslint:disable-next-line:max-func-body-length
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    ...other
}) => {
    // @ts-ignore
    const [hasHomeNumber, setHasHomeNumber] = React.useState<boolean>(false);
    // @ts-ignore
    const [shouldCheckHomeNumber, setShouldCheckHomeNumber] = React.useState<boolean>(false);
    const addressContainerRef = React.useRef<HTMLDivElement | null>(null);
    // @ts-ignore
    const resources = other?.moduleProps?.resources;
    // @ts-ignore
    const localeString = other?.moduleProps?.context?.request?.locale;
    // @ts-ignore
    const sitePath = other?.moduleProps?.context?.request?.sitePath;
    const europe = ['/fr', '/de'];
    // @ts-ignore
    const currency = other?.moduleProps?.context?.request?.channel?.Currency;
    const locale =
        sitePath && europe.includes(sitePath)
            ? 'eu'
            : currency === 'EUR' && localeString === 'en-GB'
            ? 'eu'
            : localeString.replace('-', '');

    const countriesAvail = {
        enGB: {
            autocomplete: ['gb'],
            select: ['GBR'],
            exclude: [
                { type: 'administrative_area_level_1', short_name: 'NIR', long_name: 'Northern Ireland' },
                { type: 'administrative_area_level_1', short_name: 'Northern Ireland', long_name: 'Northern Ireland' },
                { type: 'postal_code', short_name: 'BT', long_name: 'BT' }
            ]
        },
        it: {
            autocomplete: ['it'],
            select: ['ITA'],
            exclude: []
        },
        enUS: {
            autocomplete: ['us'],
            // autocomplete: ['us', 'ca'],
            //select: ['USA', 'CAN']
            select: ['USA'],
            exclude: []
        },
        eu: {
            // removed componentRestrictions for country as there is limit of 5 in google maps
            // autocomplete:['at','be','bg','ch','cz','de','dk','es','ee','fi','fr','gr','hr','hu','ie','li','lt','lu','lv','mc','nl','no','pl','pt','ro','sk','si','se'],
            autocomplete: [],
            select: [
                'AUT',
                'BEL',
                'BGR',
                'CHE',
                'CZE',
                'DEU',
                'DNK',
                'ESP',
                'EST',
                'FIN',
                'FRA',
                'GRC',
                'HRV',
                'HUN',
                'IRL',
                'LIE',
                'LTU',
                'LUX',
                'LVA',
                'MCO',
                'NLD',
                'NOR',
                'POL',
                'PRT',
                'ROU',
                'SVK',
                'SVN',
                'SWE'
            ],
            exclude: []
        },
        enSG: {
            autocomplete: ['sg'],
            select: ['SGP'],
            exclude: []
        },
        enHK: {
            autocomplete: ['hk'],
            select: ['HKG'],
            exclude: []
        },
        zhHK: {
            autocomplete: ['hk'],
            select: ['HKG'],
            exclude: []
        }
    };
    // @ts-ignore
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

    function isExcludedPlace(addressComponents: any[], excludeArr: any[] = []) {
        if (!excludeArr.length) {
            return false;
        }
        for (const ex of excludeArr) {
            for (const comp of addressComponents) {
                if (
                    comp.types.includes(ex.type) &&
                    (comp.short_name.toLowerCase().includes(ex.short_name.toLowerCase()) ||
                        comp.long_name.toLowerCase().includes(ex.long_name.toLowerCase()))
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    // tslint:disable-next-line:typedef
    function gMapsAutocomplete() {
        const googleComponents = [
            { googleComponent: 'sublocality_level_1', id: 'shipping_addresscity' },
            { googleComponent: 'locality', id: 'shipping_addresscity' },
            { googleComponent: 'postal_town', id: 'shipping_addresscity' },
            {
                googleComponent: 'administrative_area_level_3',
                id: 'shipping_addresscity'
            },
            {
                googleComponent: 'administrative_area_level_1',
                id: 'shipping_addressstate'
            },
            { googleComponent: 'postal_code_prefix', id: 'shipping_addresszipcode' },
            { googleComponent: 'postal_code', id: 'shipping_addresszipcode' },
            {
                googleComponent: 'country',
                id: 'shipping_addressthreeletterisoregionname'
            },
            {
                googleComponent: 'administrative_area_level_2',
                id: 'shipping_addresscounty'
            }
        ];

        const autocompleteFormField = document.getElementById('shipping_addressstreet');
        const addressErrorField = addressContainerRef?.current?.querySelector('.msc-address-form__alert');
        const saveAddressButton = document.querySelector('.ms-checkout__guided-card-btn-save.msc-btn') as HTMLButtonElement;

        // @ts-ignore
        const autocomplete = new window.google.maps.places.Autocomplete(autocompleteFormField, {
            types: ['address'],
            componentRestrictions: {
                country: countriesAvail[locale] && countriesAvail[locale].autocomplete
            }
        });
        if (autocompleteFormField === null) {
            return;
        }
        autocompleteFormField.setAttribute('placeholder', resources.startTypingYourAddress);
        // @ts-ignore
        window.google.maps.event.clearInstanceListeners(autocompleteFormField);
        // @ts-ignore
        window.google.maps.event.addListener(autocomplete, `place_changed`, () => {
            let homeNumberPresent = false;
            setHasHomeNumber(false);
            setShouldCheckHomeNumber(true);

            const place = autocomplete.getPlace();
            const excludeArr = countriesAvail[locale]?.exclude || [];
            if (place.address_components && isExcludedPlace(place.address_components, excludeArr)) {
                addressContainerRef?.current?.classList.add('address-form__item-invalid');
                addressErrorField!.innerHTML = `<span id='errorUnsupportedAddress'>${resources.unsupportedAddressErrorMessage ||
                    'Unsupported address'}</span>`;
                document.querySelector('#errorUnsupportedAddress')!.classList.remove('hidden');
                saveAddressButton.setAttribute('disabled', 'true');
                return;
            }
            if (nativeInputValueSetter === undefined) {
                return;
            }
            nativeInputValueSetter.call(autocompleteFormField, place.name);
            const eCustom = new Event('change', { bubbles: true });
            autocompleteFormField.dispatchEvent(eCustom);

            for (const googleComponent of googleComponents) {
                const addressComponents = place.address_components;
                if (addressComponents && addressComponents.length > 0) {
                    // @ts-ignore
                    addressComponents.forEach(addressComponent => {
                        if (addressComponent.types.includes('street_number')) {
                            homeNumberPresent = true;
                            setHasHomeNumber(true);
                        }
                        populateFormElements(addressComponent, googleComponent);
                    });

                    if (!homeNumberPresent) {
                        addressContainerRef?.current?.classList.add('address-form__item-invalid');
                        addressErrorField!.innerHTML = `<span id='errorNumberRequired'>${resources.addressHomeNumberRequiredErrorMessage}</span>`;
                        document.querySelector('#errorNumberRequired')!.classList.remove('hidden');
                        saveAddressButton.setAttribute('disabled', 'true');
                        setHasHomeNumber(true);
                    }
                }
            }
        });
    }

    // tslint:disable-next-line:typedef no-any
    function populateFormElements(
        addressComponent: { types: any[]; short_name: string; long_name: string },
        formMap: { googleComponent: any; id: string }
    ) {
        const addressType = addressComponent.types[0];

        if (document.getElementById(formMap.id) === undefined || document.getElementById(formMap.id) === null) {
            return;
        }

        if (formMap.googleComponent === addressType) {
            if (formMap.id === 'shipping_addressthreeletterisoregionname' || formMap.id === 'shipping_addressstate') {
                const input = document.getElementById(formMap.id) as HTMLSelectElement;
                if (input !== null) {
                    const formValue = locale === 'it' && formMap.id === 'shipping_addressstate' ? 'DEF' : addressComponent.short_name;
                    const selectedValue = selectItemByValue(input, formValue, addressType === 'country' ? true : false);
                    if (selectedValue !== undefined) {
                        input.value = selectedValue;
                        const event = new Event('change', { bubbles: true });
                        input.dispatchEvent(event);
                    }
                }
            } else {
                const formValue = formMap.id === 'shipping_addresscounty' ? addressComponent.short_name : addressComponent.long_name;
                const input = document.getElementById(formMap.id);
                if (input !== undefined) {
                    if (nativeInputValueSetter === undefined) {
                        return;
                    }
                    nativeInputValueSetter.call(input, formValue);
                    const eCustom = new Event('input', { bubbles: true });
                    if (input !== null) {
                        input.dispatchEvent(eCustom);
                    }
                }
            }
        }
    }

    // @ts-ignore
    // tslint:disable-next-line:typedef
    function selectItemByValue(elmnt: HTMLSelectElement, value: string, is_convert_a2_to_a3: boolean) {
        for (let i = 0; i < elmnt.options.length; i++) {
            const eValue = elmnt.options[i].value;
            if ((is_convert_a2_to_a3 && eValue === convert_a2_to_a3[value]) || (!is_convert_a2_to_a3 && eValue === value)) {
                return elmnt.options[i].value;
            }
        }
    }
    const checkPhone = () => {
        const saveAddressButton = document.querySelector('.ms-checkout__guided-card-btn-save.msc-btn') as HTMLButtonElement;
        const phone = document.getElementById('shipping_addressphone') as HTMLInputElement;
        if (phone) {
            const phoneCtn = phone.closest('.msc-address-form__item') as HTMLElement;
            const phoneAlert = phoneCtn.getElementsByClassName('msc-address-form__alert')[0];
            const phonePattern = /^(\+)?[0-9]+$/;

            phone.setAttribute('aria-required', 'true');
            phone.setAttribute('type', 'tel');
            phone.setAttribute('maxlength', '15');

            const validatePhone = () => {
                const phoneValue = phone.value.trim();

                if (!phoneValue.length) {
                    phoneCtn.classList.add('address-form__item-invalid');
                    phoneAlert.textContent = resources.addressPhoneRequiredErrorMessage;
                    saveAddressButton.setAttribute('disabled', 'true');
                    return false;
                }

                if (!phonePattern.test(phoneValue)) {
                    phoneCtn.classList.add('address-form__item-invalid');
                    phoneAlert.textContent = resources.addressPhoneRequiredErrorMessage;
                    saveAddressButton.setAttribute('disabled', 'true');
                    return false;
                }

                const digitsOnly = phoneValue.replace(/\+/g, '');
                if (digitsOnly.length < 6) {
                    phoneCtn.classList.add('address-form__item-invalid');
                    phoneAlert.textContent = resources.addressPhoneRequiredErrorMessage;
                    saveAddressButton.setAttribute('disabled', 'true');
                    return false;
                }

                phoneCtn.classList.remove('address-form__item-invalid');
                phoneAlert.textContent = '';
                saveAddressButton.removeAttribute('disabled');
                return true;
            };

            // phone.onkeyup = validatePhone;
            phone.onblur = validatePhone;
        }
    };
    function phoneValidation(): void {
        const addressForm = document.getElementsByClassName('msc-address-form')[0];
        const saveAddressButton = addressForm
            .closest('.ms-checkout__guided-card')
            ?.getElementsByClassName('ms-checkout__guided-card-btn-save')[0] as HTMLElement;
        const county = document.getElementById('shipping_addresscounty') as HTMLInputElement;
        const countyCtn = county && county !== null ? (county.closest('.msc-address-form__item') as HTMLInputElement) : undefined;
        const countyAlert = countyCtn?.getElementsByClassName('msc-address-form__alert')[0];

        if (countyCtn !== undefined) {
            countyCtn.onkeyup = () => {
                countyCtn?.classList.remove('address-form__item-invalid');
                if (countyAlert) {
                    countyAlert.textContent = '';
                }
            };
        }
        if (saveAddressButton !== undefined) {
            saveAddressButton.onclick = e => {
                const countyValid = locale === 'it' && (county.value === '' || county.value.length > 2) ? false : true;
                if (!countyValid) {
                    countyCtn?.classList.add('address-form__item-invalid');
                    if (countyAlert) {
                        countyAlert.textContent = resources.countryErrorMessage;
                    }
                    const elements = addressForm.querySelectorAll('input');
                    let fieldInALL = true;
                    elements.forEach(fieldInControl);
                    function fieldInControl(element: HTMLInputElement): void {
                        if (element.value === '' && element.name !== 'County') {
                            fieldInALL = false;
                        }
                    }
                    if (fieldInALL) {
                        e.stopPropagation();
                    }
                }
            };
        }
    }
    React.useEffect(() => {
        gMapsAutocomplete();
        const options = document.querySelectorAll('#shipping_addressthreeletterisoregionname option');
        options.forEach(o => {
            const optionV = (o as HTMLOptionElement).value;
            if (countriesAvail[locale] && !countriesAvail[locale].select.includes(optionV)) {
                o.remove();
            }
        });
        const countrySelect = document.getElementById('shipping_addressthreeletterisoregionname') as HTMLSelectElement;
        if (locale === 'enUS') {
            countrySelect.value = 'USA';
        }
        const event = new Event('change', { bubbles: true });
        countrySelect.dispatchEvent(event);
        const stateSelect = document.getElementById('shipping_addressstate') as HTMLSelectElement;
        if (locale === 'it') {
            stateSelect.value = 'DEF';
            stateSelect.dispatchEvent(event);
            stateSelect.disabled = Boolean('true');
        }
        phoneValidation();
    }, []);
    // Phone Verifications
    React.useEffect(() => {
        checkPhone();
    }, [(document?.getElementById('shipping_addressphone') as HTMLInputElement)?.value]);
    React.useEffect(() => {
        phoneValidation();
    }, [document.getElementById('shipping_addressphone')]);

    React.useEffect(() => {
        const errorNumberRequiredDom = document.querySelector('#errorNumberRequired')!;
        const addressStreetField = document?.getElementById('shipping_addressstreet') as HTMLInputElement;
        const addressErrorField = addressContainerRef?.current?.querySelector('.msc-address-form__alert');
        const saveAddressButton = document.querySelector('.ms-checkout__guided-card-btn-save.msc-btn') as HTMLButtonElement;

        if (addressErrorField && errorNumberRequiredDom) {
            errorNumberRequiredDom.classList.add('hidden');
            saveAddressButton.removeAttribute('disabled');

            if (addressStreetField!.value !== '') {
                addressContainerRef?.current?.classList.remove('address-form__item-invalid');
            }
        }
    }, [(document?.getElementById('shipping_addressstreet') as HTMLInputElement)?.value]);

    React.useEffect(() => {
        const saveAddressButton = document.querySelector('.ms-checkout__guided-card-btn-save.msc-btn') as HTMLButtonElement;
        if (hasHomeNumber) {
            saveAddressButton.removeAttribute('disabled');
        } else {
            if (shouldCheckHomeNumber) {
                saveAddressButton.setAttribute('disabled', 'true');
            }
        }

        setShouldCheckHomeNumber(false);
    }, [hasHomeNumber]);

    return (
        <Node {...AddressForm}>
            {heading}
            {items &&
                items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, alert, input } = item;
                    if (key === 'Name' && input) {
                        // @ts-ignore
                        input.props.maxLength = 34;
                    }
                    return (
                        <Node
                            {...AddressItem}
                            ref={AddressItem?.id === 'shipping_addressstreet_container' ? addressContainerRef : undefined}
                            key={key}
                        >
                            {label}
                            {alert}
                            {input}
                        </Node>
                    );
                })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps> = props => {
    const { CheckoutShippingAddress, viewState, showAddress, showAddressSelect, showAddOrUpdateAddress } = props;

    const extraTextField = props.config.paragraph;
    const extraText = extraTextField && <RichTextComponent text={extraTextField} className='ms-checkout__extratext' />;

    return (
        <Module {...CheckoutShippingAddress}>
            {extraText}
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} {...CheckoutShippingAddress} />}
        </Module>
    );
};

export default CheckoutShippingAddressView;
